import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface OpenElementTarget {
  type: string;
  name: string;
}

export function useOpenElementTarget() {
  const location = useLocation();
  return useMemo<OpenElementTarget | null>(() => {
    const value = new URLSearchParams(location.search).get('openElement');
    if (!value) return null;
    const colon = value.indexOf(':');
    if (colon < 0) return null;
    return { type: value.slice(0, colon), name: value.slice(colon + 1) };
  }, [location.search]);
}

export function useScrollToOpenElement(openElement: OpenElementTarget | null, deps: unknown[]) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!openElement) return;
    const el = document.getElementById(`elem-${openElement.type}-${openElement.name}`);
    if (!el) return;
    const timer = setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const sp = new URLSearchParams(location.search);
      sp.delete('openElement');
      navigate(
        {
          pathname: location.pathname,
          search: sp.toString() ? `?${sp.toString()}` : '',
        },
        { replace: true },
      );
    }, 100);
    return () => clearTimeout(timer);
  }, [openElement, location.pathname, location.search, navigate, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
}
